import { set, view, without, omit, lensPath, update, append } from 'ramda';
import includes from 'lodash/includes';
import pick from 'lodash/pick';
import { shallowEqual } from 'recompose';
import moment from 'moment';
import * as XLSX from 'xlsx';

import {
  initialState,
  getDepartureWithCabin,
  getCabinByDepartureAndGuest,
  isAllGuestsExpanded,
  getCabin,
  getGuestsByDeparture,
  getCabinsByDeparture,
  getGuest,
  getDepartuerAndCabinObject,
  // getAnonymousIdAndCabinForImport,
} from './selectors';

import {
  SET_RESERVATION_ID,
  SET_RESERVATION_STATUS,
  ADD_RESERVATION,
  SET_ACTIVE_DEPARTURE,
  SET_ACTIVE_CABIN,
  CHANGE_GUEST_FIRST_NAME,
  CHANGE_GUEST_LAST_NAME,
  CHANGE_GUEST_EMAIL,
  CHANGE_GUEST_SPECIAL_NEEDS,
  CHANGE_GUEST_EXTRA,
  CHANGE_GUEST_AGE,
  CHANGE_GUEST_GENDER,
  CHANGE_GUEST_TITLE,
  CHANGE_GUEST_NATIONALITY,
  CHANGE_GUEST_DATE_OF_BIRTH,
  CHANGE_GUEST_DOCUMENT_TYPE,
  CHANGE_GUEST_DOCUMENT_NUMBER,
  CHANGE_GUEST_DOCUMENT_ISSUING_COUNTRY,
  CHANGE_GUEST_DOCUMENT_EXPIRY_DATE,
  CHANGE_GUEST_ICE_PHONE_NUMBER,
  CHANGE_ALL_GUESTS_LAST_NAME,
  CHANGE_ALL_GUESTS_NATIONALITY,
  CHANGE_ALL_GUESTS_AGE,
  CHANGE_ALL_GUESTS_GENDER,
  CHANGE_ALL_GUESTS_TITLE,
  TOGGLE_ALL_PASSENGERS_EXPANDED,
  TOGGLE_GUEST_EXPANDED,
  PUT_GUEST_IN_CABIN,
  REMOVE_GUEST_FROM_CABIN,
  CLEAR_GUESTS_FROM_CABINS,
  REPLACE_GUEST,
  CHANGE_VEHICLE_REGNO,
  CHANGE_TRAILER_REGNO,
  SET_REQUIRED_FIELD,
  UNSET_REQUIRED_FIELD,
  SET_VALIDATION_ERROR,
  UNSET_VALIDATION_ERROR,
  SAVE_REQUEST,
  SAVE_SUCCESS,
  SAVE_FAILED,
  SAVE_PENDING,
  SERVER_SAVE_FAILED,
  TOGGLE_HEADER_REQUIRED_FIELD,
  SET_IMPORTED_FILE,
  UPDATE_IMPORTED_FILE,
  RESET_IMPORTED_FILE,
  SET_CORRECT_COLUMN_FROM_IMPORTED_FILE,
  REMOVE_CORRECT_COLUMN_FROM_IMPORTED_FILE,
  IMPORT_FILE_CHANGE_GUEST,
  SET_STEP_IN_PROCESS_IMPORTED_FILE,
  STEP_BACK_AND_FORTH_IN_PROCESS_IMPORTED_FILE,
  SET_ACTIVE_CABIN_FOR_IMPORTED_FILE_CABIN_DISTRIBUTOR,
  SET_ACTIVE_PERSON_FOR_IMPORTED_FILE_CABIN_DISTRIBUTOR,
  REMOVE_PERSON_FROM_PERSONS_LINKED_TO_CABIN_IMPORTED_FILE,
  ADD_ANONYMOUS_ID_AND_CABIN_FOR_IMPORT,
} from './actions';

const guestsInCabinLens = cabinId => lensPath(['cabins', cabinId, 'guests']);
const selectedCabinByGuestAndDepartureLens = (guestId, departureId) => lensPath(['guests', guestId, 'selectedCabin', departureId]);

const updateGuestAndReturnNewState = (state = initialState, guestId = '', update = () => {}) => {
  const lens = lensPath(['guests', guestId]);
  return set(lens, update(view(lens, state)), state);
};

const updateAllGuestsAndReturnNewState = (state = initialState, update = () => {}) => {
  const { guests: oldGuests, ...rest } = state;
  const guests = {};
  Object.keys(oldGuests).forEach((id) => {
    guests[id] = update(oldGuests[id]);
  });
  return {
    guests,
    ...rest,
  };
};

const toggleExpandedOnAllGuestAndReturnNewState = (state = initialState) => {
  const isExpanded = isAllGuestsExpanded(state);
  return updateAllGuestsAndReturnNewState(state, guest => ({
    ...guest,
    expanded: !isExpanded,
  }));
};

const getLinkedCabins = (state, id) => getCabin(state, id).linkedCabins || [];

const removeGuestFromCabin = (state, { cabinId, guestId }) => {
  const departureId = getDepartureWithCabin(state, cabinId);
  const cabinLens = guestsInCabinLens(cabinId);
  const guestLens = selectedCabinByGuestAndDepartureLens(guestId, departureId);
  const guests = view(cabinLens, state) || [];
  return set(cabinLens, without(guestId, guests), set(guestLens, null, state));
};

const removeGuestFromCabinAndLinkedCabins = (oldState, { cabinId, guestId }) =>
  [cabinId, ...getLinkedCabins(oldState, cabinId)].reduce((aggregatedState, linkedCabinId) => removeGuestFromCabin(aggregatedState, { cabinId: linkedCabinId, guestId }), oldState);

const putGuestInCabin = (oldState, { cabinId, guestId }) => {
  const departureId = getDepartureWithCabin(oldState, cabinId);
  const oldCabinId = getCabinByDepartureAndGuest(oldState, departureId, guestId);
  if (oldCabinId === cabinId) {
    return oldState;
  }
  const state = oldCabinId
    ? removeGuestFromCabin(oldState, { cabinId: oldCabinId, guestId })
    : oldState;
  const cabinLens = guestsInCabinLens(cabinId);
  const guestLens = selectedCabinByGuestAndDepartureLens(guestId, departureId);
  const guests = view(cabinLens, state) || [];
  return set(cabinLens, [...guests, guestId], set(guestLens, cabinId, state));
};

const clearGuestFromCabins = (state, { departureId }) => {
  const cabinsClearedState = getCabinsByDeparture(state, departureId)
    .reduce((currentState, cabinId) => set(guestsInCabinLens(cabinId), [], currentState), state);
  const guestsClearedState = getGuestsByDeparture(state, departureId)
    .reduce((currentState, guestId) => set(selectedCabinByGuestAndDepartureLens(guestId, departureId), null, currentState), cabinsClearedState);
  return guestsClearedState;
};

const isGuestOnDeparture = (state, { cabinId, guestId }) =>
  includes(getGuestsByDeparture(state, getDepartureWithCabin(state, cabinId)), guestId);

const putGuestInCabinIfGuestIsOnDeparture = (state, payload) =>
  isGuestOnDeparture(state, payload)
    ? putGuestInCabin(state, payload)
    : state;

const putGuestInCabinAndLinkedCabins = (oldState, { cabinId, guestId }) => [cabinId, ...getLinkedCabins(oldState, cabinId)].reduce((aggregatedState, linkedCabinId) => putGuestInCabinIfGuestIsOnDeparture(aggregatedState, { cabinId: linkedCabinId, guestId }), oldState);

const replaceGuest = (state, { oldId, newGuest }) => {
  const departures = {};
  Object.keys(state.departures).forEach((id) => {
    const departure = state.departures[id];
    departures[id] = {
      ...departure,
      guests: departure.guests.map(g => g === oldId ? newGuest.id : g),
    };
  });

  const oldGuest = getGuest(state, oldId);
  const cabinId = oldGuest.selectedCabin ? Object.values(oldGuest.selectedCabin) : null;
  const firstCabin = (cabinId && cabinId.length >= 1 && cabinId[0]) || '';
  const secondCabin = (cabinId && cabinId.length > 1 && cabinId[1]) || '';
  if (firstCabin && secondCabin) {
    return {
      ...state,
      departures,
      guests: {
        ...omit(oldId, state.guests),
        [newGuest.id]: {
          ...omit('isAnonymous', oldGuest),
          id: newGuest.id,
        },
      },
      replacedGuests: {
        ...state.replaceGuests,
        [oldId]: newGuest.id,
      },
      cabins: {
        ...state.cabins,
        [firstCabin]: {
          ...state.cabins[firstCabin],
          guests: append(newGuest.id, state.cabins[firstCabin].guests),
        },
        [secondCabin]: {
          ...state.cabins[secondCabin],
          guests: append(newGuest.id, state.cabins[secondCabin].guests),
        },
      },
    };
  }
  if (firstCabin && !secondCabin) {
    return {
      ...state,
      departures,
      guests: {
        ...omit(oldId, state.guests),
        [newGuest.id]: {
          ...omit('isAnonymous', oldGuest),
          id: newGuest.id,
        },
      },
      replacedGuests: {
        ...state.replaceGuests,
        [oldId]: newGuest.id,
      },
      cabins: {
        ...state.cabins,
        [firstCabin]: {
          ...state.cabins[firstCabin],
          guests: append(newGuest.id, state.cabins[firstCabin].guests),
        },
      },
    };
  }
  return {
    ...state,
    departures,
    guests: {
      ...omit(oldId, state.guests),
      [newGuest.id]: {
        ...omit('isAnonymous', oldGuest),
        id: newGuest.id,
      },
    },
    replacedGuests: {
      ...state.replaceGuests,
      [oldId]: newGuest.id,
    },
  };
};

const changeVehicleRegNo = (state, { id, regNo }) => set(lensPath(['vehicles', id, 'regNo']), regNo, state);

const changeTrailerRegNo = (state, { id, regNo }) => set(lensPath(['vehicles', id, 'trailer', 'regNo']), regNo, state);

const saveStatus = status => (state, payload) => set(lensPath([payload.resource, payload.id, 'saveStatus']), status, state);
const saveSuccess = saveStatus('SUCCESS');
const saveFailure = saveStatus('FAILED');
const savePending = saveStatus('PENDING');

const serverSaveStatus = () => (state, payload) => set(lensPath(['serverSaveStatus']), { status: payload.response, saveTime: payload.saveTime }, state);

const serverSaveSuccess = serverSaveStatus('SUCCESS');
const serverSavePending = serverSaveStatus('PENDING');
const serverSaveFailed = serverSaveStatus('FAILED');

const setRequiredFieldsNotCompleted = (state, { guestId, field }) => {
  const lens = lensPath(['guests', guestId, 'requiredFieldsNotCompleted']);
  return set(lens, [...without([field], view(lens, state) || []), field], state);
};

const unsetRequiredFieldsNotCompleted = (state, { guestId, field }) => {
  const lens = lensPath(['guests', guestId, 'requiredFieldsNotCompleted']);
  return set(lens, without([field], view(lens, state) || []), state);
};

const setFieldsNotValid = (state, { guestId, field }) => {
  const lens = lensPath(['guests', guestId, 'validationFieldsNotCompleted']);
  return set(lens, [...without([field], view(lens, state) || []), field], state);
};

const unsetFieldsNotValid = (state, { guestId, field }) => {
  const lens = lensPath(['guests', guestId, 'validationFieldsNotCompleted']);
  return set(lens, without([field], view(lens, state) || []), state);
};

const setTitleFromDragListImportedFile = (state, { column, title }) => {
  const lens = lensPath(['importedFile', 'titleOnColumnNumber']);
  return set(lens, update(parseInt(column, 10), title, view(lens, state)), state);
};

const removeTitleFromDragListImportedFile = (state, { column }) => {
  const lens = lensPath(['importedFile', 'titleOnColumnNumber']);
  return set(lens, update(parseInt(column, 10), null, view(lens, state)), state);
};

const setImportedFile = (state, { data }) => {
  const isStenaTemplate = data.stenaTemplate;
  return {
    ...state,
    importedFile: {
      titleOnColumnNumber: data.formattedFile[0],
      data: [...data.formattedFile],
      stenaTemplate: isStenaTemplate,
    },
  };
};

const formatDate = (value) => {
  if (typeof value === 'number') {
    // Excel date cells arrive as serial numbers. Build the calendar date from its parts, never via Date/timestamps.
    const { y, m, d } = XLSX.SSF.parse_date_code(value);
    return moment({ year: y, month: m - 1, day: d }).format(moment.HTML5_FMT.DATE);
  }
  const localeDateFormat = moment.localeData().longDateFormat('L');
  const divider = localeDateFormat.replace(/\w/g, '').charAt(0);
  const splitValue = value.split(/[^0-9]/);
  const splitLocaleDateFormat = localeDateFormat.split(/[^A-Z]/);
  const getTwoDigitYear = new Date().getFullYear().toString().substr(-2);
  let reversed = false;
  if ((splitValue && parseInt(splitValue[0], 10) > 31) || (splitLocaleDateFormat[0].length > 2 && splitValue && parseInt(splitValue[2], 10) > 31)) {
    splitValue.reverse();
    reversed = true;
  }
  if (!reversed && splitValue && parseInt(splitValue[2], 10) > getTwoDigitYear && splitValue[2].length < 3) {
    // eslint-disable-next-line prefer-template
    splitValue[2] = '19' + splitValue[2];
  }
  if (reversed && splitValue && parseInt(splitValue[0], 10) > getTwoDigitYear && splitValue[0].length < 3) {
    // eslint-disable-next-line prefer-template
    splitValue[0] = '19' + splitValue[0];
  }
  const formattedValue = splitValue.join(divider);
  // Plain calendar date (YYYY-MM-DD), never a timestamp carrying the browser's time zone offset.
  return moment(formattedValue, localeDateFormat).format(moment.HTML5_FMT.DATE);
};

const importFileChangeGuest = (state, { guestId, resource, value }) => {
  let payloadData = { guestId, [resource]: value };
  if (resource.toLowerCase() === 'dateofbirth') {
    payloadData = { guestId, [resource]: formatDate(value) };
  }
  if (resource.toLowerCase() === 'age') {
    payloadData = { guestId, customerCategory: value };
  }
  if (resource.toLowerCase() === 'cabin') {
    payloadData = { guestId, selectedCabin: getDepartuerAndCabinObject(state, value) };
  }

  return updateGuestAndReturnNewState(state, guestId, guest => ({
    ...guest,
    ...payloadData,
  }));
};

const setActivePersonForImportedCabinDistributor = (state, { obj }) => {
  const cabinId = state.importedFile.activeCabinInCabinDistributor;
  if (cabinId) {
    const lensToLinkedPersonCabin = lensPath(['importedFile', 'personLinkedToCabin']);
    const object = { guests: obj, cabin: cabinId };
    const objectToRemove = state.importedFile.personLinkedToCabin && state.importedFile.personLinkedToCabin
    .filter(linked => (JSON.stringify(linked.guests.id) === JSON.stringify(obj.guests && obj.guests.id)) || (JSON.stringify(linked.cabin) === JSON.stringify(cabinId)));
    return set(lensToLinkedPersonCabin, [...without(objectToRemove, view(lensToLinkedPersonCabin, state) || []), object], state);
  }
  return {
    ...state,
  };
};

const removePersonFromPersonsLinkedToCabinImportedFile = (state, { cabinId }) => {
  const lensToLinkedPersonCabin = lensPath(['importedFile', 'personLinkedToCabin']);
  const objectToRemove = state.importedFile.personLinkedToCabin && state.importedFile.personLinkedToCabin
  .filter(linked => (JSON.stringify(linked.cabin) === JSON.stringify(cabinId)));
  return set(lensToLinkedPersonCabin, [...without(objectToRemove, view(lensToLinkedPersonCabin, state) || [])], state);
};

export default (state = initialState, { type, payload = {} }) => {
  const { id, ...payloadData } = payload;
  switch (type) {
    case SET_RESERVATION_ID:
      return {
        ...state,
        ...payload,
      };
    case SET_RESERVATION_STATUS:
      return {
        ...state,
        ...payload,
      };
    case ADD_RESERVATION:
      return {
        ...state,
        ...payload,
        activeDeparture: (payload.departureIds && payload.departureIds.length && payload.departureIds[0]) || '',
      };
    case SET_ACTIVE_DEPARTURE:
      return {
        ...state,
        activeDeparture: id,
      };
    case SET_ACTIVE_CABIN:
      return {
        ...state,
        activeCabin: {
          ...state.activeCabin,
          [payload.departureId]: id,
        },
      };
    case TOGGLE_GUEST_EXPANDED:
      return updateGuestAndReturnNewState(state, id, guest => ({
        ...guest,
        expanded: !guest.expanded,
      }));
    case CHANGE_GUEST_FIRST_NAME:
    case CHANGE_GUEST_LAST_NAME:
    case CHANGE_GUEST_EMAIL:
    case CHANGE_GUEST_SPECIAL_NEEDS:
    case CHANGE_GUEST_EXTRA:
    case CHANGE_GUEST_AGE:
    case CHANGE_GUEST_GENDER:
    case CHANGE_GUEST_TITLE:
    case CHANGE_GUEST_NATIONALITY:
    case CHANGE_GUEST_DATE_OF_BIRTH:
    case CHANGE_GUEST_DOCUMENT_TYPE:
    case CHANGE_GUEST_DOCUMENT_NUMBER:
    case CHANGE_GUEST_DOCUMENT_ISSUING_COUNTRY:
    case CHANGE_GUEST_DOCUMENT_EXPIRY_DATE:
    case CHANGE_GUEST_ICE_PHONE_NUMBER:
      return updateGuestAndReturnNewState(state, id, guest => ({
        ...guest,
        ...payloadData,
      }));
    case CHANGE_ALL_GUESTS_LAST_NAME:
    case CHANGE_ALL_GUESTS_NATIONALITY:
    case CHANGE_ALL_GUESTS_AGE:
    case CHANGE_ALL_GUESTS_GENDER:
    case CHANGE_ALL_GUESTS_TITLE:
      return updateAllGuestsAndReturnNewState(state, guest =>
        shallowEqual(pick(guest, Object.keys(payloadData)), payloadData)
        ? guest
        : {
          ...guest,
          ...payloadData,
        },
      );
    case TOGGLE_ALL_PASSENGERS_EXPANDED:
      return toggleExpandedOnAllGuestAndReturnNewState(state);
    case PUT_GUEST_IN_CABIN:
      return putGuestInCabinAndLinkedCabins(state, payload);
    case REMOVE_GUEST_FROM_CABIN:
      return removeGuestFromCabinAndLinkedCabins(state, payload);
    case CLEAR_GUESTS_FROM_CABINS:
      return clearGuestFromCabins(state, payload);
    case REPLACE_GUEST:
      return replaceGuest(state, payload);
    case CHANGE_VEHICLE_REGNO:
      return changeVehicleRegNo(state, payload);
    case CHANGE_TRAILER_REGNO:
      return changeTrailerRegNo(state, payload);
    case SET_REQUIRED_FIELD:
      return setRequiredFieldsNotCompleted(state, payload);
    case UNSET_REQUIRED_FIELD:
      return unsetRequiredFieldsNotCompleted(state, payload);
    case SET_VALIDATION_ERROR:
      return setFieldsNotValid(state, payload);
    case UNSET_VALIDATION_ERROR:
      return unsetFieldsNotValid(state, payload);
    case SAVE_REQUEST:
      return savePending(state, payload);
    case SAVE_SUCCESS:
      return saveSuccess(serverSaveSuccess(state, payload), payload);
    case SAVE_FAILED:
      return saveFailure(serverSaveFailed(state, payload), payload);
    case SAVE_PENDING:
      return savePending(serverSavePending(state, payload), payload);
    case SERVER_SAVE_FAILED:
      return serverSaveFailed(state, payload);
    case TOGGLE_HEADER_REQUIRED_FIELD:
      return {
        ...state,
        headerRequiredFieldIsOpen: !state.headerRequiredFieldIsOpen,
      };
    case SET_IMPORTED_FILE:
      return setImportedFile(state, payload);
    case UPDATE_IMPORTED_FILE:
      return {
        ...state,
        importedFile: {
          ...state.importedFile,
          data: [...payload.file],
        },
      };
    case RESET_IMPORTED_FILE:
      return {
        ...state,
        importedFile: {
          titleOnColumnNumber: [],
          data: [],
        },
      };
    case SET_CORRECT_COLUMN_FROM_IMPORTED_FILE:
      return setTitleFromDragListImportedFile(state, payload);
    case REMOVE_CORRECT_COLUMN_FROM_IMPORTED_FILE:
      return removeTitleFromDragListImportedFile(state, payload);
    case IMPORT_FILE_CHANGE_GUEST:
      return importFileChangeGuest(state, payload);
    case SET_STEP_IN_PROCESS_IMPORTED_FILE:
      return {
        ...state,
        importedFileStepProcess: {
          stepsProcess: payload.steps,
          activeStep: 0,
        },
      };
    case STEP_BACK_AND_FORTH_IN_PROCESS_IMPORTED_FILE:
      return {
        ...state,
        importedFileStepProcess: {
          ...state.importedFileStepProcess,
          activeStep: payload.number < 0 ? 0 : payload.number,
        },
      };
    case SET_ACTIVE_CABIN_FOR_IMPORTED_FILE_CABIN_DISTRIBUTOR:
      return {
        ...state,
        importedFile: {
          ...state.importedFile,
          activeCabinInCabinDistributor: payload.id,
        },
      };
    case SET_ACTIVE_PERSON_FOR_IMPORTED_FILE_CABIN_DISTRIBUTOR:
      return setActivePersonForImportedCabinDistributor(state, payload);
    case REMOVE_PERSON_FROM_PERSONS_LINKED_TO_CABIN_IMPORTED_FILE:
      return removePersonFromPersonsLinkedToCabinImportedFile(state, payload);
    case ADD_ANONYMOUS_ID_AND_CABIN_FOR_IMPORT:
      return {
        ...state,
        importedFile: {
          ...state.importedFile,
          anonymousIdAndCabinForImport: append(payload.obj, state.importedFile.anonymousIdAndCabinForImport),
        },
      };
    default:
      return state;
  }
};



// WEBPACK FOOTER //
// ./src/store/reservation/reducer.js